import { CommonModule } from '@angular/common';
import { Component, Inject, Injectable, Input, OnInit, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Platform } from '@angular/cdk/platform';
import {
    DateAdapter,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
    MatDateFormats,
    MatNativeDateModule,
    NativeDateAdapter,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import Swal from 'sweetalert2';

export const ES_CO_DATE_FORMATS: MatDateFormats = {
    parse: {
        dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
    },
    display: {
        dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
        monthYearLabel: { year: 'numeric', month: 'short' },
        dateA11yLabel: { day: 'numeric', month: 'long', year: 'numeric' },
        monthYearA11yLabel: { year: 'numeric', month: 'long' },
    },
};

@Injectable()
export class EsCoDateAdapter extends NativeDateAdapter {
    constructor(
        @Inject(MAT_DATE_LOCALE) matDateLocale: string,
        platform: Platform
    ) {
        super(matDateLocale, platform);
    }

    override parse(value: unknown): Date | null {
        if (typeof value === 'string' && value.trim()) {
            const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
            if (match) {
                const dia = Number(match[1]);
                const mes = Number(match[2]) - 1;
                const anio = Number(match[3]);
                const fecha = new Date(anio, mes, dia);
                return this.isValid(fecha) && fecha.getDate() === dia
                    ? fecha
                    : this.invalid();
            }
        }
        return super.parse(value as string);
    }

    override format(date: Date, displayFormat: unknown): string {
        if (!this.isValid(date)) {
            return '';
        }
        if (
            displayFormat &&
            typeof displayFormat === 'object' &&
            (displayFormat as { day?: string }).day === '2-digit'
        ) {
            const dia = String(date.getDate()).padStart(2, '0');
            const mes = String(date.getMonth() + 1).padStart(2, '0');
            return `${dia}/${mes}/${date.getFullYear()}`;
        }
        return super.format(date, displayFormat);
    }
}

@Component({
    selector: 'app-input-date',
    standalone: true,
    imports: [CommonModule, MatDatepickerModule, MatNativeDateModule],
    templateUrl: './input-date.component.html',
    styleUrls: ['./input-date.component.css'],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'es-CO' },
        { provide: DateAdapter, useClass: EsCoDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: ES_CO_DATE_FORMATS },
    ],
})
export class InputDateComponent implements ControlValueAccessor, OnInit {
    @Input() label = '';
    @Input() inputId = '';
    @Input() placeholder = 'dd/mm/aaaa';
    @Input() tooltip = '';
    @Input() required = false;
    @Input() disabled = false;
    @Input() submitted = false;
    @Input() requiredError: string | null = null;
    @Input() min: Date | string | null = null;
    @Input() max: Date | string | null = null;

    value: Date | null = null;
    private blurred = false;

    constructor(
        @Optional() @Self() public ngControl: NgControl,
        private dateAdapter: DateAdapter<Date>
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit(): void {
        this.dateAdapter.setLocale('es-CO');
        this.requiredError =
            this.requiredError ?? `Campo ${this.label} requerido`;
    }

    get minDate(): Date | null {
        return this.toDate(this.min);
    }

    get maxDate(): Date | null {
        return this.toDate(this.max);
    }

    get invalid(): boolean {
        return !!this.ngControl?.control?.invalid;
    }

    get touched(): boolean {
        return this.blurred || !!this.ngControl?.touched;
    }

    get showError(): boolean {
        return (this.submitted || this.touched) && this.invalid;
    }

    onChange: (value: Date | null) => void = () => {};
    onTouched: () => void = () => {};

    writeValue(value: Date | string | null): void {
        this.value = this.toDate(value);
    }

    registerOnChange(fn: (value: Date | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onDateChange(fecha: Date | null): void {
        this.value = fecha;
        this.onChange(fecha);
    }

    onBlur(): void {
        this.blurred = true;
        this.onTouched();
    }

    mostrarTooltip(): void {
        if (!this.tooltip) {
            return;
        }
        Swal.fire('', this.tooltip, 'info');
    }

    private toDate(valor: Date | string | null | undefined): Date | null {
        if (!valor) {
            return null;
        }
        if (valor instanceof Date) {
            return Number.isNaN(valor.getTime()) ? null : valor;
        }
        const [anio, mes, dia] = valor.split('T')[0].split('-').map(Number);
        if (!anio || !mes || !dia) {
            return null;
        }
        return new Date(anio, mes - 1, dia);
    }
}
